// Mirrors backend/src/types.ts. PRODUCTION: share via @blisspointmedia/bpm-types.

export type AuditStatus = 'pending' | 'pulling' | 'analyzing' | 'complete' | 'failed';

export type Platform = 'google_ads' | 'microsoft_ads';

export const PLATFORM_LABEL: Record<Platform, string> = {
  google_ads: 'Google',
  microsoft_ads: 'Microsoft'
};

export const ALL_PLATFORMS: Platform[] = ['google_ads', 'microsoft_ads'];

export type Template = 'search' | 'shopping' | 'pmax';

export type Severity = 'large' | 'medium' | 'small' | 'no_issue' | 'na';

// Canonical section order for grouped display. Sections not listed here fall to the end,
// alphabetical. Add to this list when introducing new audit sections.
export const SECTION_ORDER: string[] = [
  'Account Structure',
  'Conversions',
  'Bidding & Budgets',
  'Audiences',
  'Ad Copy',
  'Keywords',
  'Negatives',
  'Audience Signals',
  'Creative Excellence',
  'Assets',
  'Bidding',
  'Budgets',
  'Other Campaign Settings',
  'Geo & Schedule',
  'Devices',
  'Match Types',
  'Feed Health',
  'Performance Diagnostics'
];

export function sectionRank(section: string): number {
  const idx = SECTION_ORDER.indexOf(section);
  return idx === -1 ? SECTION_ORDER.length : idx;
}

export type Triage =
  'untriaged' | 'will_address' | 'acknowledged' | 'discuss' | 'by_design' | 'wont_fix';

export type FindingStatus = 'unavailable';

export interface CidSummary {
  cid: string;
  name: string;
  mcc: string;
  platform: Platform;
  activeTemplates: Template[];
}

export interface AuditRun {
  id: string;
  company: string;
  title: string;
  triggeredBy: string;
  triggeredAt: string;
  completedAt: string | null;
  status: AuditStatus;
  windowStart: string;
  windowEnd: string;
  cids: CidSummary[];
  error: string | null;
}

export interface LlmVerdict {
  draft: string;
  confidence: 'high' | 'medium' | 'low';
  groundedIn: string[];
}

export type FindingDiffStatus = 'new' | 'changed' | 'unchanged';

export interface FindingDiff {
  status: FindingDiffStatus;
  previousSeverity?: Severity;
  previousTriage?: Triage;
  previousStatus?: FindingStatus | null;
  underlyingChanged?: boolean;
}

export interface Finding {
  id: string;
  auditRunId: string;
  platform: Platform;
  cid: string;
  template: Template;
  section: string;
  checkId: string;
  templateRow: string;
  severity: Severity;
  status: FindingStatus | null;
  statusReason: string | null;
  isJudgmentRow: boolean;
  evidence: Record<string, unknown>;
  llmVerdict: LlmVerdict | null;
  // Optional "look here first" summary rendered at the top of the evidence card. Markdown.
  // Null when the check doesn't opt into a summary.
  summary: string | null;
  triage: Triage;
  auditorNote: string;
  updatedAt: string;
  // Present when the backend was able to compare this audit against a prior complete run for
  // the same company. `null` means no previous audit existed (first run).
  diff: FindingDiff | null;
}

export interface PreviousAuditSummary {
  id: string;
  triggeredAt: string;
}

export interface AuditDetailResponse {
  run: AuditRun;
  findings: Finding[];
  previousAudit: PreviousAuditSummary | null;
}

// ----- Slice 6: dedicated "What changed" diff (mirrors backend) -----
export type DiffTransition =
  'resolved' | 'worsened' | 'improved' | 'new' | 'removed' | 'still_open' | 'unchanged';

export interface AuditDiffEntry {
  cid: string;
  platform: Platform;
  template: Template;
  section: string;
  checkId: string;
  templateRow: string;
  transition: DiffTransition;
  currentSeverity: Severity | null;
  priorSeverity: Severity | null;
  availabilityChanged: boolean;
  current: Finding | null;
  prior: Finding | null;
}

export interface AuditDiffRunRef {
  id: string;
  triggeredAt: string;
  title: string;
}

export interface AuditDiffResult {
  current: AuditDiffRunRef;
  prior: AuditDiffRunRef | null;
  entries: AuditDiffEntry[];
  counts: Record<DiffTransition, number>;
}

// Ordinal rank for severity used by the diff UI to decide direction (up = worse, down = better).
// "na" is treated as the most neutral state.
const SEVERITY_RANK: Record<Severity, number> = {
  na: 0,
  no_issue: 1,
  small: 2,
  medium: 3,
  large: 4
};

export function severityRank(s: Severity): number {
  return SEVERITY_RANK[s];
}
